import { Box, Stack, Typography } from "@mui/material";

import { formatRecipeSectors } from "../utils/recipeSector.utils";

const UNIT_LABELS = {
  g: "g",
  ml: "ml",
  unit: "unidad",
  package: "paquete",
};

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return String(value);
  return numberValue.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

function formatQuantity(value, unit) {
  const quantity = formatNumber(value);
  if (quantity === "-") return quantity;
  return unit ? `${quantity} ${UNIT_LABELS[unit] || unit}` : quantity;
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{ fontSize: 17, fontWeight: 800, color: "primary.900" }}>
      {children}
    </Typography>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "120px 1fr" },
        gap: { xs: 0.35, sm: 1.2 },
        alignItems: "center",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "neutral.700" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900" }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function StatusPill({ active }) {
  return (
    <Box
      sx={{
        width: 84,
        px: 1,
        py: 0.55,
        borderRadius: "20px",
        bgcolor: active ? "rgba(33, 150, 83, 0.16)" : "rgba(195, 60, 60, 0.16)",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
      }}
    >
      <Typography
        sx={{
          color: active ? "rgba(18, 111, 63, 1)" : "rgba(160, 49, 49, 1)",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {active ? "Activo" : "Inactivo"}
      </Typography>
    </Box>
  );
}

function ComponentLine({ name, quantity, notes }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 112px" },
        gap: 1,
        alignItems: "start",
        py: 1,
        borderBottom: "1px solid rgba(0,0,0,.07)",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap title={name} sx={{ fontSize: 14, fontWeight: 800, color: "primary.900" }}>
          {name}
        </Typography>
        {notes ? (
          <Typography sx={{ mt: 0.25, fontSize: 12, fontWeight: 600, color: "neutral.700" }}>
            {notes}
          </Typography>
        ) : null}
      </Box>
      <Typography
        sx={{
          justifySelf: { xs: "start", sm: "end" },
          minWidth: 86,
          px: 1.2,
          py: 0.45,
          borderRadius: "16px",
          bgcolor: "secondary.100",
          color: "primary.900",
          fontSize: 12,
          fontWeight: 800,
          textAlign: "center",
        }}
      >
        {quantity}
      </Typography>
    </Box>
  );
}

function EmptyState({ children }) {
  return (
    <Typography sx={{ py: 1.2, fontSize: 13, fontWeight: 700, color: "neutral.700" }}>
      {children}
    </Typography>
  );
}

export default function RecipeReadOnlyView({ recipe, version, loading = false }) {
  const ingredients = Array.isArray(version?.ingredients) ? version.ingredients : [];
  const preparedProducts = Array.isArray(version?.preparedProducts)
    ? version.preparedProducts
    : [];

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        px: { xs: 2, lg: 3 },
        py: { xs: 2, lg: 2.5 },
        bgcolor: "neutral.50",
        borderRadius: "22px",
      }}
    >
      {loading ? (
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "neutral.700" }}>
          Cargando receta...
        </Typography>
      ) : (
        <Stack spacing={2.4} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900", textTransform: "uppercase" }}>
              Vista de receta
            </Typography>
            <StatusPill active={recipe?.isActive} />
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.35fr" },
              gap: { xs: 2.5, lg: 3 },
              alignItems: "stretch",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.75,
              scrollbarGutter: "stable",
            }}
          >
            <Stack spacing={2}>
              <SectionTitle>Información general</SectionTitle>
              <Stack spacing={1.3}>
                <InfoRow label="Nombre" value={recipe?.name} />
                <InfoRow label="Sector" value={formatRecipeSectors(recipe?.sector)} />
              </Stack>

              <Box sx={{ pt: 1 }}>
                <SectionTitle>Rendimiento</SectionTitle>
              </Box>
              {version ? (
                <Box sx={{ px: 1.4, py: 1, borderRadius: "16px", bgcolor: "secondary.50" }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "neutral.700" }}>
                    Cantidad de porciones
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                    {formatNumber(version.portionQuantity)}
                  </Typography>
                </Box>
              ) : (
                <EmptyState>Esta receta todavía no tiene una versión cargada.</EmptyState>
              )}
            </Stack>

            <Stack
              spacing={1.5}
              sx={{
                minHeight: 0,
                alignSelf: "stretch",
                pl: { xs: 0, lg: 3 },
                borderLeft: { xs: "none", lg: "2px solid rgba(2, 8, 23, 0.45)" },
              }}
            >
              <Box sx={{ minHeight: 0, flex: { xs: "none", lg: 1 }, display: "flex", flexDirection: "column" }}>
                <SectionTitle>Lista de ingredientes</SectionTitle>
                <Box sx={{ mt: 1, minHeight: 0, maxHeight: { xs: 260, lg: "none" }, overflowY: "auto", pr: 0.8 }}>
                  <Stack spacing={1}>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                      Insumos
                    </Typography>
                    {ingredients.length ? (
                      ingredients.map((line) => (
                        <ComponentLine
                          key={line.id}
                          name={line.ingredient?.name || `Insumo #${line.ingredientId}`}
                          quantity={formatQuantity(line.quantity, line.ingredient?.baseUnit)}
                          notes={line.notes}
                        />
                      ))
                    ) : (
                      <EmptyState>La receta no tiene ingredientes cargados.</EmptyState>
                    )}

                    <Typography sx={{ pt: 0.8, fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                      Elaborados
                    </Typography>
                    {preparedProducts.length ? (
                      preparedProducts.map((line) => (
                        <ComponentLine
                          key={line.id}
                          name={line.preparedProduct?.name || `Elaborado #${line.preparedProductId}`}
                          quantity={formatQuantity(line.quantity, line.preparedProduct?.resultUnit)}
                          notes={line.notes}
                        />
                      ))
                    ) : (
                      <EmptyState>La receta no tiene elaborados cargados.</EmptyState>
                    )}
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ minHeight: 0, flex: { xs: "none", lg: 1 }, display: "flex", flexDirection: "column" }}>
                <SectionTitle>Pasos de la receta</SectionTitle>
                <Box
                  sx={{
                    mt: 1,
                    minHeight: { xs: 130, lg: 0 },
                    maxHeight: { xs: 260, lg: "none" },
                    flex: { xs: "none", lg: 1 },
                    overflowY: "auto",
                    px: 1.4,
                    py: 1.2,
                    borderRadius: "16px",
                    bgcolor: "neutral.100",
                    color: "neutral.800",
                    fontSize: 13,
                    fontWeight: 650,
                    "& p": { my: 0.5 },
                    "& ul, & ol": { my: 0.5, pl: 2.5 },
                    "& img": { maxWidth: "100%", height: "auto" },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: version?.notes || "<p>Sin notas de preparación.</p>",
                  }}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
